// dashboard/views/ActivationChecklistView.js
//
// FIX (2026-09-17, clinic onboarding/activation audit, finding COA2):
// a new Manager-only founder (create_clinic_self_serve creates the
// founder as active Manager-only, no Doctor seat consumed) previously
// landed on DashboardView's `!canClinical` branch, which renders
// "Patient data is restricted to clinic staff with clinical access" /
// "Contact your account administrator if you need clinical access." The
// founder IS the administrator — that message is a dead end, not a
// restriction notice. This replaces it, for the Manager-only case
// specifically, with an activation checklist that explains the actual
// required order (subscribe/select seats -> add or assign Clinical
// Access -> invite clinicians -> enroll a patient) and links straight to
// each step. DashboardView still falls back to the original restricted
// message for the (currently not really reachable in practice, but
// defensively preserved) case of a non-Doctor, non-Manager membership.
//
// Done-state derivation, and why each one is safe/best-effort:
// - Clinic/ER info: `clinic.phone` + at least one emergency hospital
//   name filled in. These are the only clinic-detail fields this
//   dashboard actually has today (no street/city/state/ZIP columns
//   exist on `clinics` despite COA5's audit text describing them —
//   confirmed via migration history; not something this fix invents).
// - Billing: `subscription.status` is 'active'/'trialing'. Subscription
//   is normally only fetched lazily when the Billing tab is opened
//   (App.js's own effect, gated on `view === 'billing'`) — this view
//   explicitly calls the passed-down `refreshSubscription` on mount so
//   the checklist has real data without requiring a tab visit first.
// - Seats: `subscription.seat_count > 0` — distinct from Billing being
//   merely "active" (a healthy subscription could theoretically still
//   have its seat count at the plan minimum before any Doctor is added).
// - Clinical Access: `vets.length > 0` — vets (active Doctors) is
//   already fetched unconditionally on clinic load (App.js's
//   fetchGlobalData), so no new fetch needed.
// - Staff: full roster length > 1 (more than just the founder) —
//   fetched here via the same `get_clinic_roster` RPC PermissionsView
//   already uses; not available from App.js's existing state, since
//   `vets` only holds active Doctors, not the whole roster.
// - Printable: no done-state. There's no "was this downloaded" flag to
//   check, and inventing one isn't worth it for a checklist item whose
//   whole job is "here's where the printable lives" — matches the
//   instruction to leave undeterminable items as static navigation.
// - First patient: `pendingPets.length > 0` is used as a best-effort
//   proxy, not a reliable "has an active patient" signal. RLS blocks
//   Manager-only (non-Doctor) reads of `patients`/`conditions` entirely
//   (confirmed via this session's own RLS audit history — clinical data
//   requires `is_active_clinic_doctor`), so a real enrolled-patient count
//   is structurally invisible to this exact viewer. `pendingPets` (QR/
//   enrollment-code scans awaiting confirmation) IS visible to a Manager
//   and is a reasonable "something has started" signal — it just can't
//   distinguish "pending" from "already confirmed and now invisible to
//   me," so this can under-report done but will never falsely claim done
//   for a clinic with zero enrollment activity at all.

import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { c } from '../constants';

function ChecklistItem({ label, detail, done, onGo, t }) {
  return (
    <button
      onClick={onGo}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        textAlign: 'left', padding: '14px 16px', marginBottom: 8,
        backgroundColor: c.white, border: `1px solid ${c.border}`, borderRadius: 10,
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      <span style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        backgroundColor: done === true ? c.moss : c.white,
        border: `1.5px solid ${done === true ? c.moss : c.border}`,
        color: c.white, fontSize: 13, fontWeight: 700,
      }}>
        {done === true ? '✓' : ''}
      </span>
      <span style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: c.slate }}>{label}</div>
        {detail && <div style={{ fontSize: 12, color: c.textSoft, marginTop: 2 }}>{detail}</div>}
      </span>
      <span style={{ fontSize: 13, color: c.moss, fontWeight: 600, flexShrink: 0 }}>
        {done === true ? t('activationChecklist.review') : t('activationChecklist.go')} →
      </span>
    </button>
  );
}

export default function ActivationChecklistView({
  clinic, subscription, subscriptionLoaded, refreshSubscription,
  vets, pendingPets, setView,
}) {
  const { t } = useTranslation();
  // null = not loaded yet, distinct from 0 (loaded, founder-only roster)
  const [rosterCount, setRosterCount] = useState(null);

  useEffect(() => {
    if (clinic?.id && !subscriptionLoaded) refreshSubscription(clinic.id);
  }, [clinic?.id, subscriptionLoaded, refreshSubscription]);

  useEffect(() => {
    if (!clinic?.id) return;
    let cancelled = false;
    supabase.rpc('get_clinic_roster', { p_clinic_id: clinic.id }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) { console.warn('ActivationChecklistView roster fetch failed:', error.message); return; }
      setRosterCount((data || []).length);
    });
    return () => { cancelled = true; };
  }, [clinic?.id]);

  const clinicInfoDone = !!(clinic?.phone && (clinic?.emergency_hospital_1_name || clinic?.emergency_hospital_2_name));
  const billingDone    = subscription?.status === 'active' || subscription?.status === 'trialing';
  const seatsDone      = (subscription?.seat_count || 0) > 0;
  const clinicalDone   = (vets?.length || 0) > 0;
  const staffDone      = rosterCount !== null && rosterCount > 1;
  const patientDone    = (pendingPets?.length || 0) > 0;

  return (
    <div style={{ backgroundColor: c.fog, borderRadius: 12, padding: 32, maxWidth: 640 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: c.slate, margin: '0 0 6px' }}>
        {t('activationChecklist.title')}
      </h1>
      <p style={{ fontSize: 14, color: c.textSoft, margin: '0 0 24px' }}>
        {t('activationChecklist.subtitle')}
      </p>

      <ChecklistItem t={t} done={clinicInfoDone}
        label={t('activationChecklist.clinicInfoLabel')}
        detail={t('activationChecklist.clinicInfoDetail')}
        onGo={() => setView('settings')} />

      <ChecklistItem t={t} done={billingDone}
        label={t('activationChecklist.billingLabel')}
        detail={t('activationChecklist.billingDetail')}
        onGo={() => setView('billing')} />

      <ChecklistItem t={t} done={seatsDone}
        label={t('activationChecklist.seatsLabel')}
        detail={t('activationChecklist.seatsDetail')}
        onGo={() => setView('billing')} />

      <ChecklistItem t={t} done={clinicalDone}
        label={t('activationChecklist.clinicalAccessLabel')}
        detail={t('activationChecklist.clinicalAccessDetail')}
        onGo={() => setView('permissions')} />

      <ChecklistItem t={t} done={staffDone}
        label={t('activationChecklist.staffLabel')}
        detail={t('activationChecklist.staffDetail')}
        onGo={() => setView('permissions')} />

      <ChecklistItem t={t} done={null}
        label={t('activationChecklist.printableLabel')}
        detail={t('activationChecklist.printableDetail')}
        onGo={() => setView('settings')} />

      <ChecklistItem t={t} done={patientDone}
        label={t('activationChecklist.firstPatientLabel')}
        detail={t('activationChecklist.firstPatientDetail')}
        onGo={() => setView('enrollments')} />
    </div>
  );
}
